import { useState, useEffect, useCallback, useMemo, useRef, type ReactNode } from 'react';
import apiClient from '../services/api';
import { AuthContext, type User } from './AuthContext';

export default function AuthProvider({ children }: { children: ReactNode }) {
    const [authState, setAuthState] = useState<{
        isLoggedIn: boolean;
        user: User | null;
        loading: boolean;
    }>({
        isLoggedIn: false,
        user: null,
        loading: true,
    });

    const hasCheckedRef = useRef(false);

    const login = useCallback(() => {
        window.location.href = '/login';
    }, []);

    const logout = useCallback(() => {
        window.location.href = '/logout';
    }, []);

    useEffect(() => {
        if (hasCheckedRef.current) return;
        hasCheckedRef.current = true;

        const checkAuth = async () => {
            try {
                const response = await apiClient.get<User & { isAuthenticated: boolean }>('/user');
                if (response.status === 200 && response.data.isAuthenticated) {
                    setAuthState({
                        isLoggedIn: true,
                        user: response.data,
                        loading: false,
                    });
                } else {
                    setAuthState({
                        isLoggedIn: false,
                        user: null,
                        loading: false,
                    });
                }
            } catch (error) {
                console.error('Failed to check auth status:', error);
                setAuthState({
                    isLoggedIn: false,
                    user: null,
                    loading: false,
                });
            }
        };

        checkAuth();
    }, []);

    const value = useMemo(() => ({
        ...authState,
        login,
        logout,
    }), [authState, login, logout]);

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};
